import React, { useState } from 'react';
import { NavLink as ReactRouterLink } from 'react-router-dom';
import { AccountBox, Logout, Menu as MenuIcon } from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Toolbar,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Grid from '@mui/material/Grid';
import { useAuth } from 'shared/hooks';
import AppearanceMenu from 'shared/components/AppearanceMenu';
import ThemeModeToggle from 'shared/components/ThemeModeToggle';
import ICSLogo128 from 'shared/assets/ICS_Logo_128.png';
import profilePath from 'shared/routes/home';

const NavBarWrapper = styled(AppBar)(({ theme }) => ({
  position: 'fixed',
  right: 0,
  zIndex: theme.zIndex.nav,
  backgroundColor: (theme.vars || theme).palette.navBackground,
  padding: '0 1rem',
  boxShadow: (theme.vars || theme).palette.elevation.overlay,
}));

const NavLogoLink = styled(ReactRouterLink)(({ theme }) => ({
  display: 'flex',
  height: '85px',
  marginRight: '16px',
  padding: '0 30px 0 8px',
  alignItems: 'center',
  color: (theme.vars || theme).palette.navTextActive,
  textDecoration: 'none',
  ':hover': {
    color: (theme.vars || theme).palette.navTextActive,
  },
  '& > img': {
    height: '75px',
  },
  '& > h2': {
    margin: '0 0 0 16px',
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },
}));

const DesktopNav = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const ProfileDropdownButton = styled(Button)(({ theme }) => ({
  marginRight: '8px',
  color: (theme.vars || theme).palette.navText,
  fontSize: '16px',
  fontWeight: 400,
  textTransform: 'none',
  flexShrink: 0,
  ':hover': {
    color: (theme.vars || theme).palette.navTextHover,
  },
}));

const ProfileMenuLink = styled(ReactRouterLink)({
  color: 'unset',
  ':hover': {
    color: 'unset',
  },
  '&.active': {
    textDecoration: 'underline',
  },
});

const MobileNav = styled(Box)(({ theme }) => ({
  zIndex: theme.zIndex.nav,
  display: 'none',
  '& > *': {
    color: (theme.vars || theme).palette.navText,
  },
  [theme.breakpoints.down('md')]: {
    display: 'block !important',
  },
}));

const MobileNavMenuButton = styled(Button)(({ theme }) => ({
  color: (theme.vars || theme).palette.navText,
  border: `1px solid ${(theme.vars || theme).palette.navBorder}`,
}));

const NavLink = styled(ReactRouterLink)(({ theme }) => ({
  padding: '8px',
  fontSize: '16px',
  fontWeight: 400,
  color: (theme.vars || theme).palette.navText,
  ':hover': {
    color: (theme.vars || theme).palette.navTextHover,
  },
  transition: 'color 0.3s ease',
  textTransform: 'capitalize !important',
  textDecoration: 'none',
  '&.active': {
    color: (theme.vars || theme).palette.navAccent,
  },
}));

/**
 * @param {Object} props
 * @param {Array<*>} props.routes
 */
export default function NavBar({ routes }) {
  const { logout, user, isLoggedIn } = useAuth();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(undefined);
  const open = Boolean(anchorEl);

  const toggleDrawer = (inOpen) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    setMobileDrawerOpen(inOpen);
  };

  const ProfileDropdown = (
    <>
      <ProfileDropdownButton
        id="profile-dropdown"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={(e) => { setAnchorEl(e.currentTarget); }}
      >
        {user.username}
        <KeyboardArrowDownIcon fontSize="small" />
      </ProfileDropdownButton>
      <Menu
        id="profile-dropdown"
        open={open}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(undefined); }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
          paper: { sx: { minWidth: 220 } },
        }}
      >
        <ProfileMenuLink
          onClick={() => {
            setAnchorEl(undefined);
            setMobileDrawerOpen(false);
          }}
          to={profilePath}
        >
          <MenuItem>
            <ListItemIcon><AccountBox color="action" fontSize="small" /></ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>
        </ProfileMenuLink>
        <Divider />
        <AppearanceMenu />
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(undefined);
            setMobileDrawerOpen(false);
            logout();
          }}
        >
          <ListItemIcon><Logout color="action" fontSize="small" /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );

  const MobileNavMenu = (
    <Grid container sx={{ pt: 12, pl: 2 }}>
      {routes.map(({ path, name, rootPath }) => (
        <Grid size={12} key={path}>
          <NavLink
            exact
            isActive={(match, location) => location.pathname.split('/')[1] === rootPath.split('/')[1]}
            onClick={() => setMobileDrawerOpen(false)}
            to={path}
          >
            {name}
          </NavLink>
        </Grid>
      ))}
      {ProfileDropdown}
    </Grid>
  );

  return (
    <NavBarWrapper>
      <Toolbar sx={{ padding: '0 !important', justifyContent: 'space-between' }}>
        <NavLogoLink onClick={() => setMobileDrawerOpen(false)} to="/">
          <img alt="Logo" src={ICSLogo128} />
          <Typography variant="h2">Reactor</Typography>
        </NavLogoLink>
        {!isLoggedIn && (
          <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
            <ThemeModeToggle color="navText" />
          </Box>
        )}

        {isLoggedIn && (
        <>
          <DesktopNav>
            <Box sx={{ typography: 'body1' }}>
              {routes.map(({ path, name, rootPath }) => (
                <NavLink
                  isActive={(match, location) => location.pathname.split('/')[1] === rootPath.split('/')[1]}
                  exact
                  key={path}
                  to={path}
                >
                  {name}
                </NavLink>
              ))}
            </Box>
            {ProfileDropdown}
          </DesktopNav>
          <MobileNav>
            <MobileNavMenuButton onClick={toggleDrawer(!mobileDrawerOpen)}>
              <MenuIcon fontSize="large" />
            </MobileNavMenuButton>
          </MobileNav>
          <Drawer
            open={mobileDrawerOpen}
            sx={{ zIndex: (theme) => theme.zIndex.drawer, typography: 'body1', height: '50vh !important' }}
            anchor="top"
            onClose={toggleDrawer(false)}
            slotProps={{
              paper: { sx: { backgroundColor: 'navBackground' } },
            }}
          >
            {MobileNavMenu}
          </Drawer>
        </>
        )}
      </Toolbar>
    </NavBarWrapper>
  );
}
